// src/utils/riskCalculator.ts - Risk scoring utility for BCPs
import { BCP } from '../services/api';

export type RiskLevel = 'Low' | 'Medium' | 'High' | 'Critical';

export interface RiskScore {
  level: RiskLevel;
  score: number; // 0-100
  factors: {
    completeness: number;
    riskAssessment: number;
    businessImpact: number;
    criticalOperations: number;
  };
  recommendations: string[];
}

/**
 * Calculate risk score for a BCP based on various factors
 */
export function calculateRiskScore(bcp: BCP): RiskScore {
  const factors = {
    completeness: calculateCompletenessScore(bcp),
    riskAssessment: calculateRiskAssessmentScore(bcp),
    businessImpact: calculateBusinessImpactScore(bcp),
    criticalOperations: calculateCriticalOperationsScore(bcp)
  };

  // Weighted average of all factors
  const weights = {
    completeness: 0.3,      // 30% - How complete is the BCP
    riskAssessment: 0.25,   // 25% - Quality of risk assessment
    businessImpact: 0.25,   // 25% - Business impact analysis
    criticalOperations: 0.2 // 20% - Critical operations coverage
  };

  const totalScore = Math.round(
    factors.completeness * weights.completeness +
    factors.riskAssessment * weights.riskAssessment +
    factors.businessImpact * weights.businessImpact +
    factors.criticalOperations * weights.criticalOperations
  );

  const level = getRiskLevel(totalScore);
  const recommendations = generateRecommendations(factors, level);

  return {
    level,
    score: totalScore,
    factors,
    recommendations
  };
}

/**
 * Calculate completeness score (0-100)
 * Based on how much of the BCP is filled out
 */
function calculateCompletenessScore(bcp: BCP): number {
  let score = 0;
  let maxScore = 0;

  // Basic business info (20 points)
  maxScore += 20;
  if (bcp.business_name) score += 5;
  if (bcp.business_type) score += 5;
  if (bcp.industry) score += 5;
  if (bcp.employee_count && bcp.employee_count > 0) score += 5;

  // Location info (15 points)
  maxScore += 15;
  if (bcp.address) score += 5;
  if (bcp.primary_location?.name) score += 5;
  if (bcp.primary_location?.address) score += 5;

  // Risk assessment (25 points)
  maxScore += 25;
  if (bcp.risk_factors?.selected?.length) score += 12;
  if (bcp.threat_probability?.selected?.length) score += 13;

  // Business impact analysis (20 points)
  maxScore += 20;
  if (bcp.financial_impact_severity) score += 5;
  if (bcp.operational_impact_severity) score += 5;
  if (bcp.reputational_impact_severity) score += 5;
  if (bcp.legal_regulatory_impact_severity) score += 5;

  // Critical operations (20 points)
  maxScore += 20;
  if (bcp.critical_functions?.length) score += 7;
  if (bcp.key_suppliers?.length) score += 6;
  if (bcp.it_systems?.length) score += 7;

  return maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
}

/**
 * Calculate risk assessment quality score (0-100)
 * Based on the quality and depth of risk assessment
 */
function calculateRiskAssessmentScore(bcp: BCP): number {
  let score = 0;

  // Risk factors coverage
  const riskFactors = bcp.risk_factors?.selected || [];
  const threatFactors = bcp.threat_probability?.selected || [];

  // More risks identified = higher score (up to 50 points)
  const totalRisks = riskFactors.length + threatFactors.length;
  score += Math.min(totalRisks * 5, 50);

  // Risk impact assessments (if available)
  if (bcp.risk_impact_assessments) {
    const assessments = bcp.risk_impact_assessments;
    const primaryConcerns = assessments.primary_concerns || {};
    const locationRisks = assessments.location_risks || {};

    const totalAssessments = Object.keys(primaryConcerns).length + Object.keys(locationRisks).length;
    score += Math.min(totalAssessments * 10, 30);
  }

  // Risk mitigation planning (if max_downtime and recovery_priority are set)
  if (bcp.max_downtime && bcp.recovery_priority) {
    score += 20;
  }

  return Math.min(score, 100);
}

/**
 * Calculate business impact analysis score (0-100)
 * Based on BIA severity levels and descriptions
 */
function calculateBusinessImpactScore(bcp: BCP): number {
  let score = 0;

  // Check if BIA severity levels are set
  const severityFields = [
    bcp.financial_impact_severity,
    bcp.operational_impact_severity,
    bcp.reputational_impact_severity,
    bcp.legal_regulatory_impact_severity
  ];

  const severityCount = severityFields.filter(field => field).length;
  score += severityCount * 15; // 15 points per severity level

  // Check if descriptions are provided
  const descriptionFields = [
    bcp.financial_impact_description,
    bcp.operational_impact_description,
    bcp.reputational_impact_description,
    bcp.legal_regulatory_impact_description
  ];

  const descriptionCount = descriptionFields.filter(field => field && field.trim().length > 10).length;
  score += descriptionCount * 10; // 10 points per detailed description

  return Math.min(score, 100);
}

/**
 * Calculate critical operations score (0-100)
 * Based on coverage of critical functions, suppliers, and IT systems
 */
function calculateCriticalOperationsScore(bcp: BCP): number {
  let score = 0;

  // Critical functions (40 points)
  const criticalFunctions = bcp.critical_functions || [];
  if (criticalFunctions.length > 0) {
    score += Math.min(criticalFunctions.length * 8, 40);
  }

  // Key suppliers (30 points)
  const keySuppliers = bcp.key_suppliers || [];
  if (keySuppliers.length > 0) {
    score += Math.min(keySuppliers.length * 10, 30);
  }

  // IT systems (30 points)
  const itSystems = bcp.it_systems || [];
  if (itSystems.length > 0) {
    score += Math.min(itSystems.length * 10, 30);
  }

  return Math.min(score, 100);
}

/**
 * Convert numeric score to risk level
 */
function getRiskLevel(score: number): RiskLevel {
  if (score >= 80) return 'Low';
  if (score >= 60) return 'Medium';
  if (score >= 40) return 'High';
  return 'Critical';
}

/**
 * Generate recommendations based on risk factors
 */
function generateRecommendations(factors: any, level: RiskLevel): string[] {
  const recommendations: string[] = [];

  if (factors.completeness < 50) {
    recommendations.push('Complete missing BCP sections to improve risk assessment');
  }

  if (factors.riskAssessment < 40) {
    recommendations.push('Conduct comprehensive risk assessment with more identified risks');
  }

  if (factors.businessImpact < 40) {
    recommendations.push('Complete Business Impact Analysis with severity assessments');
  }

  if (factors.criticalOperations < 40) {
    recommendations.push('Document critical functions, suppliers, and IT systems');
  }

  if (level === 'Critical' || level === 'High') {
    recommendations.push('Consider immediate BCP review and updates');
  }

  if (recommendations.length === 0) {
    recommendations.push('BCP appears well-developed. Consider regular reviews and updates.');
  }

  return recommendations;
}

/**
 * Calculate overall risk score for multiple BCPs
 */
export function calculateOverallRiskScore(bcps: BCP[]): RiskScore {
  if (bcps.length === 0) {
    return {
      level: 'Critical',
      score: 0,
      factors: {
        completeness: 0,
        riskAssessment: 0,
        businessImpact: 0,
        criticalOperations: 0
      },
      recommendations: ['Create your first BCP to begin risk assessment']
    };
  }

  // Calculate average scores across all BCPs
  const totalScores = bcps.reduce((acc, bcp) => {
    const score = calculateRiskScore(bcp);
    return {
      completeness: acc.completeness + score.factors.completeness,
      riskAssessment: acc.riskAssessment + score.factors.riskAssessment,
      businessImpact: acc.businessImpact + score.factors.businessImpact,
      criticalOperations: acc.criticalOperations + score.factors.criticalOperations
    };
  }, { completeness: 0, riskAssessment: 0, businessImpact: 0, criticalOperations: 0 });

  const avgFactors = {
    completeness: Math.round(totalScores.completeness / bcps.length),
    riskAssessment: Math.round(totalScores.riskAssessment / bcps.length),
    businessImpact: Math.round(totalScores.businessImpact / bcps.length),
    criticalOperations: Math.round(totalScores.criticalOperations / bcps.length)
  };

  // Weighted average
  const totalScore = Math.round(
    avgFactors.completeness * 0.3 +
    avgFactors.riskAssessment * 0.25 +
    avgFactors.businessImpact * 0.25 +
    avgFactors.criticalOperations * 0.2
  );

  const level = getRiskLevel(totalScore);
  const recommendations = generateRecommendations(avgFactors, level);

  return {
    level,
    score: totalScore,
    factors: avgFactors,
    recommendations
  };
}
